<template>
	<EventLayout :event="event" :subtitle="trans('events.sponsors.title')">
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="showModal('create-sponsor-modal')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<Modal id="create-sponsor-modal">
			<template #title>{{ trans('events.sponsors.create') }}</template>
			<div class="flex flex-col gap-3">
				<LabeledObject required full>
					<template #label>{{ trans('events.sponsors.name') }}</template>
					<input v-model="sponsor.name" type="text">
				</LabeledObject>
				<LabeledObject required full>
					<template #label>{{ trans('events.sponsors.file') }}</template>
					<input type="file" accept="image/jpeg,image/png" @change="loadFile($event)">
				</LabeledObject>
			</div>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="store">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<Empty v-if="sponsors.length === 0"/>
		<CardGrid v-else tight>
			<Card v-for="sp in sponsors" :key="`sponsor-${sp.id}`" :title="sp.name" :image="sp.file" has-image>
				<template #footer>
					<SoftButton :color="Color.Red" @click.stop="remove(sp.id)">
						<i class="fa-solid fa-trash mr-2"/>
						{{ trans('general.delete') }}
					</SoftButton>
				</template>
			</Card>
		</CardGrid>
	</EventLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import {defineProps, ref, type Ref} from 'vue'
import Card from '@/Components/UI/Cards/Card.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import {type Sponsor} from '@/Types/Events/Sponsor'
import EventLayout from '@/Layouts/EventLayout.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import {type Event as AArEIIEvent} from '@/Types/Events/Event'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	event:AArEIIEvent,
}>()

const sponsor:Ref<{
	name:string,
	file?:Blob,
	event_id:number,
}> = ref({
	name: '',
	file: undefined,
	event_id: props.event.id,
})
const working:Ref<boolean> = ref(false)
const event:Ref<AArEIIEvent> = ref(props.event)
const sponsors:Ref<Array<Sponsor>> = ref(props.event.sponsors)

function loadFile(event:Event):void{
	if(event.target && event.target instanceof HTMLInputElement && event.target.files){
		sponsor.value.file = event.target.files[0]
	}
}

function store():void{
	if(!working.value && sponsor.value.file){
		working.value = true
		showInfo(trans('general.inserting'))
		const formData = new FormData
		formData.append('name', sponsor.value.name)
		formData.append('file', sponsor.value.file)
		axios.post(route('events.sponsors.store', event.value.id), formData).then(({data}) => {
			sponsors.value.unshift(data)
			showSuccess(trans('general.inserted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function remove(sponsorId:number):void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('events.sponsors.delete', {event: event.value.id, sponsor: sponsorId})).then(() => {
			const index = sponsors.value.findIndex((s:Sponsor) => s.id === sponsorId)
			if(index > -1){
				sponsors.value.splice(index, 1)
			}
			showSuccess(trans('general.deleted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>